/**
 * Dashboard Page for Admin TMA
 * Displays KPI cards and event feed
 * Design: iOS/SaaS style from GPT.html + GasworkersAdmin_Real.html
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useStats,
  useFeed,
  formatMoney,
  formatRelativeTime,
  EVENT_ACTION_LABELS,
  EVENT_ACTION_COLORS,
} from '@/hooks/useDashboard';
import {
  Briefcase,
  TrendingUp,
  TrendingDown,
  Users,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
  UserCheck,
  Phone,
  DollarSign,
  PlusCircle,
  RotateCcw,
} from 'lucide-react';

// Icon mapping for events
const EVENT_ICONS: Record<string, React.ReactNode> = {
  MASTER_STATUS_CHANGED: <UserCheck className="h-4 w-4" />,
  CONTACTS_REVEALED: <Phone className="h-4 w-4" />,
  ORDER_DISPUTED: <AlertCircle className="h-4 w-4" />,
  ORDER_CREATED: <PlusCircle className="h-4 w-4" />,
  ORDER_STATUS_CHANGED: <RefreshCw className="h-4 w-4" />,
  PAYMENT_RECEIVED: <CreditCard className="h-4 w-4" />,
  PAYMENT_REFUNDED: <RotateCcw className="h-4 w-4" />,
  BID_PLACED: <DollarSign className="h-4 w-4" />,
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useStats();
  const { data: feed, isLoading: feedLoading, refetch: refetchFeed } = useFeed({ limit: 10 });

  const handleRefresh = () => {
    refetchStats();
    refetchFeed();
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const goToMasters = () => {
    navigate('/admin/verification');
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const goToOrders = () => {
    navigate('/admin/orders');
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  // Format today's date
  const today = new Date();
  const dateFormatted = today.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  });

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <section className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-800">Обзор дня</h1>
        <p className="text-sm text-slate-500 capitalize">{dateFormatted}</p>
      </section>

      {/* KPI Cards */}
      <section className="grid grid-cols-2 gap-3">
        {/* Active Orders Card */}
        <Card
          className="cursor-pointer transition-transform active:scale-[0.98] bg-white border-slate-100 shadow-sm"
          onClick={goToOrders}
        >
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                <Briefcase className="h-5 w-5" />
              </div>
              {stats && stats.ordersDelta !== 0 && (
                <Badge
                  variant="outline"
                  className={`text-xs font-bold ${
                    stats.ordersDelta > 0
                      ? 'bg-green-50 text-green-600 border-green-100'
                      : 'bg-red-50 text-red-600 border-red-100'
                  }`}
                >
                  {stats.ordersDelta > 0 ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  )}
                  {stats.ordersDelta > 0 ? '+' : ''}
                  {stats.ordersDelta}%
                </Badge>
              )}
            </div>
            <div>
              {statsLoading ? (
                <div className="h-8 w-16 bg-slate-100 rounded animate-pulse" />
              ) : (
                <span className="text-3xl font-bold text-slate-800 block">
                  {stats?.activeOrders ?? 0}
                </span>
              )}
              <span className="text-xs text-slate-500 font-medium">Активных заказов</span>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Card */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                <CreditCard className="h-5 w-5" />
              </div>
            </div>
            <div>
              {statsLoading ? (
                <div className="h-8 w-24 bg-slate-100 rounded animate-pulse" />
              ) : (
                <span className="text-2xl font-bold text-slate-800 block">
                  {stats ? formatMoney(stats.revenueToday) : '0 ₽'}
                </span>
              )}
              <span className="text-xs text-slate-500 font-medium">Оборот сегодня</span>
            </div>
          </CardContent>
        </Card>

        {/* Pending Masters Card - Full Width */}
        {stats && stats.pendingMasters > 0 && (
          <Card
            className="col-span-2 cursor-pointer transition-transform active:scale-[0.98] bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg border-0"
            onClick={goToMasters}
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-white/90 text-sm font-medium mb-1">Требуют внимания</p>
                  <p className="text-2xl font-bold">
                    {stats.pendingMasters} {stats.pendingMasters === 1 ? 'новый мастер' : 'новых мастера'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <ArrowRight className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Online Masters Card */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                {statsLoading ? (
                  <div className="h-6 w-8 bg-slate-100 rounded animate-pulse" />
                ) : (
                  <span className="text-xl font-bold text-slate-800 block">
                    {stats?.onlineMasters ?? 0}
                  </span>
                )}
                <span className="text-xs text-slate-500">Онлайн</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders Today Card */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center text-violet-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                {statsLoading ? (
                  <div className="h-6 w-8 bg-slate-100 rounded animate-pulse" />
                ) : (
                  <span className="text-xl font-bold text-slate-800 block">
                    {stats?.ordersToday ?? 0}
                  </span>
                )}
                <span className="text-xs text-slate-500">Заказов сегодня</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Event Feed */}
      <section className="space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-slate-700">Лента событий</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="text-blue-600 text-xs font-semibold hover:bg-blue-50"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Обновить
          </Button>
        </div>

        <Card className="bg-white border-slate-100 shadow-sm overflow-hidden">
          {feedLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex gap-3 items-start animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-slate-200 mt-2" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-100 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : feed && feed.data.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {feed.data.map((event) => (
                <div key={event.id} className="p-4 flex gap-3 items-start">
                  <div
                    className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                      EVENT_ACTION_COLORS[event.action] || 'bg-slate-400'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      {EVENT_ACTION_LABELS[event.action] || event.action}
                    </p>
                    {event.user && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {event.user.name}
                        {event.orderId && (
                          <span className="text-slate-400 ml-1">
                            #{event.orderId.slice(0, 8)}
                          </span>
                        )}
                      </p>
                    )}
                    {event.details && typeof event.details === 'object' && 'newStatus' in event.details && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Статус: <span className="text-slate-700">{String(event.details.newStatus)}</span>
                      </p>
                    )}
                    <span className="text-[10px] text-slate-400 mt-1 block">
                      {formatRelativeTime(event.createdAt)}
                    </span>
                  </div>
                  <div className="shrink-0 text-slate-400">
                    {EVENT_ICONS[event.action] || <CheckCircle className="h-4 w-4" />}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">Нет событий</p>
            </div>
          )}
        </Card>

        {feed && feed.pagination.total > 10 && (
          <p className="text-xs text-slate-400 text-center">
            Показано {feed.data.length} из {feed.pagination.total} событий
          </p>
        )}
      </section>
    </div>
  );
}

export default DashboardPage;
